<template>
  <div
    :class="
      isMobile && focused
        ? 'fixed md:absolute top-0 w-full h-full bg-white left-0 px-4 pt-2 z-50'
        : 'relative'
    "
  >
    <div class="flex items-center space-x-2">
      <div
        v-if="focused && isMobile"
        class="px-3 py-2"
        @click="(focused = false), (query = '')"
      >
        <ChevronIcon />
      </div>
      <TextField
        class="flex-grow"
        :label="label"
        :value="modelValue ? modelValue[itemText] : query"
        :disabled="loading || disabled"
        :class="{ 'animate-pulse': loading }"
        :show-change-button="showChangeButton"
        @focus="focused = true"
        @blur="!isMobile ? (focused = false) : ''"
        @input="onQueryChange($event.target.value)"
        @change="onSearch"
        @edit-value="$emit('edit-value')"
      />
    </div>
    <div
      v-if="showResults"
      class="
        dropdown-content
        z-10
        top-0
        w-full
        rounded-b-3xl
        overflow-hidden overflow-y-auto
        transform
        drop-shadow-dropdown
        filter
      "
      :class="{
        'relative translate-y-0': isMobile,
        'translate-y-11 absolute  border border-primary border-opacity-10 bg-white':
          !isMobile,
      }"
    >
      <ul
        :class="{ 'max-h-56': !isMobile, 'h-full': isMobile }"
        @mouseover="resultsHover = true"
        @mouseout="resultsHover = false"
      >
        <li
          v-for="result in items"
          :key="result[itemKey]"
          class="hover:bg-gray-3 cursor-pointer select-none"
          @click.prevent="onChange(result)"
        >
          <template v-if="!!$scopedSlots['item']">
            <slot name="item" v-bind="{ item: result }"></slot>
          </template>
          <template v-else>
            <div
              class="py-3 text-sm leading-6"
              :class="{ 'px-5': !isMobile, 'px-2': isMobile }"
            >
              {{ result[itemText] }}
            </div>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Emit,
  Model,
  Prop,
  Vue,
  Watch,
} from 'vue-property-decorator';
import TextField from '@/components/legacy/TextField.vue';
import ChevronIcon from '@/components/ChevronIcon.vue';
import { LocaleMessages } from 'vue-i18n';
import isMobile from '@/utils/isMobile';

@Component({
  components: {
    TextField,
    ChevronIcon,
  },
})
export default class AutocompleteField extends Vue {
  @Model('change') modelValue!: any;
  @Prop({ type: [String, Object] }) label?: string | LocaleMessages;
  @Prop({ type: Array, required: true }) items!: any[];
  @Prop({ type: String, default: 'name' }) itemText!: string;
  @Prop({ type: String, default: 'id' }) itemKey!: string;
  @Prop({ type: Function }) filter?: (
    item: any,
    queryText: string,
    itemText: string
  ) => any;
  @Prop({ type: Boolean, default: false }) loading!: boolean;
  @Prop({ type: Boolean, default: false }) disabled!: boolean;
  @Prop({ type: Boolean, default: false }) showChangeButton!: string;

  private query: string = '';
  private focused: boolean = false;
  private resultsHover: boolean = false;
  private isMobile: boolean = false;
  private windowHeight: number = 0;

  private get showResults(): boolean {
    return !!this.items.length && (this.focused || this.resultsHover);
  }

  private created() {
    this.isMobile = isMobile();
    this.windowHeight = window.innerHeight;
    if (this.isMobile && window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        this.onContainerSizeChange
      );
    }
  }

  beforeDestroy() {
    window.visualViewport?.removeEventListener(
      'resize',
      this.onContainerSizeChange
    );
  }

  @Emit('search')
  onSearch(value: any): any {
    this.query = value;
    return value;
  }

  @Emit('change')
  onChange(value: any): any {
    if (this.isMobile) this.focused = false;
    this.resultsHover = false;
    return value;
  }

  @Watch('focused')
  onFocusedChange(current: boolean) {
    if (current && !this.query) {
      this.$emit('focused');
    }
  }

  @Watch('modelValue')
  private onModelValueChange(current: any) {
    if (current) {
      this.query = '';
    }
  }

  private onContainerSizeChange() {
    if (window.innerHeight !== this.windowHeight) {
      window.scroll(0, 0);
      this.windowHeight = window.innerHeight;
    }
  }

  onQueryChange(value: string) {
    this.onChange(null);
    this.query = value;
  }
}
</script>
<style>
@media (max-width: 769px) {
  .dropdown-content {
    height: inherit;
    max-height: calc(100% - 50px);
  }
}
</style>
